/**
 * Temporary Meta Embedded Signup diagnostics.
 * Active only when VITE_META_DEBUG === "true". Otherwise every export is a no-op.
 */

import { isExpectedMetaMessageOrigin } from "./embedded-signup-session.js";

const FB_SDK_URL = "https://connect.facebook.net/en_US/sdk.js";
const MAX_EVENTS = 100;

const SENSITIVE_KEY =
  /^(authorizationCode|authorization_code|accessToken|access_token|code|signedRequest|signed_request|cookie|password|secret|token|rawPayload|raw_payload|event\.data|eventData)$/i;

export type MetaDebugEventName =
  | "META_SDK_LOAD_STARTED"
  | "META_SDK_LOAD_SUCCEEDED"
  | "META_SDK_LOAD_FAILED"
  | "META_FB_INIT"
  | "META_CONFIG_RECEIVED"
  | "META_EMBEDDED_SIGNUP_STARTED"
  | "META_FB_LOGIN_OPTIONS"
  | "META_FB_LOGIN_CALLBACK"
  | "META_WINDOW_MESSAGE_RECEIVED"
  | "META_EMBEDDED_SIGNUP_ERROR"
  | "META_EMBEDDED_SIGNUP_FINISH"
  | "META_EMBEDDED_SIGNUP_CANCEL";

export interface MetaDebugEvent {
  ts: string;
  event: MetaDebugEventName;
  correlationId?: string;
  data: Record<string, unknown>;
}

interface MetaDebugSnapshot {
  enabled: true;
  events: MetaDebugEvent[];
  copy: () => Promise<void>;
}

declare global {
  interface Window {
    __MULTI_CHAT_META_DEBUG__?: MetaDebugSnapshot;
  }
}

const events: MetaDebugEvent[] = [];
let activeCorrelationId: string | null = null;
let attemptStartedAtMs: number | null = null;
let messageListenerAttached = false;

function readMetaDebugEnabled(): boolean {
  try {
    return import.meta.env.VITE_META_DEBUG === "true";
  } catch {
    return false;
  }
}

export function isMetaDebugEnabled(): boolean {
  return readMetaDebugEnabled();
}

export function getFbSdkUrl(): string {
  return FB_SDK_URL;
}

export function createMetaDebugCorrelationId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // fall through
  }
  return `meta-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function beginMetaEmbeddedSignupAttempt(): string {
  const correlationId = createMetaDebugCorrelationId();
  activeCorrelationId = correlationId;
  attemptStartedAtMs = Date.now();
  return correlationId;
}

export function getActiveMetaDebugCorrelationId(): string | null {
  return activeCorrelationId;
}

export function getMetaEmbeddedSignupElapsedMs(): number | null {
  if (attemptStartedAtMs == null) return null;
  return Date.now() - attemptStartedAtMs;
}

/** Deep-copy and strip known secret-bearing keys. Exported for unit tests. */
export function sanitizeMetaDebugData(value: unknown, depth = 0): unknown {
  if (depth > 8) return "[max-depth]";
  if (value == null) return value;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return value;
  }
  if (typeof value === "bigint") return value.toString();
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeMetaDebugData(item, depth + 1));
  }
  if (typeof value !== "object") {
    return typeof value;
  }
  const out: Record<string, unknown> = {};
  for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
    if (SENSITIVE_KEY.test(key)) {
      out[key] = nested == null ? nested : "[redacted]";
      continue;
    }
    out[key] = sanitizeMetaDebugData(nested, depth + 1);
  }
  return out;
}

export function sanitizeMetaError(error: unknown): { name: string; message: string } {
  if (error instanceof Error) {
    return {
      name: String(error.name || "Error").slice(0, 200),
      message: String(error.message || "").slice(0, 500),
    };
  }
  return {
    name: "UnknownError",
    message: typeof error === "string" ? error.slice(0, 500) : "non-error throw",
  };
}

export function summarizeFbInitOptions(options: {
  appId?: string;
  version?: string;
  xfbml?: boolean;
  cookie?: boolean;
  status?: boolean;
  autoLogAppEvents?: boolean;
}): Record<string, unknown> {
  return {
    appId: options.appId ?? null,
    version: options.version ?? null,
    xfbml: Object.prototype.hasOwnProperty.call(options, "xfbml") ? options.xfbml : "(absent)",
    cookie: Object.prototype.hasOwnProperty.call(options, "cookie") ? options.cookie : "(absent)",
    status: Object.prototype.hasOwnProperty.call(options, "status") ? options.status : "(absent)",
    autoLogAppEvents: Object.prototype.hasOwnProperty.call(options, "autoLogAppEvents")
      ? options.autoLogAppEvents
      : "(absent)",
  };
}

export function summarizeFbLoginOptions(options: Record<string, unknown>): Record<string, unknown> {
  const extras =
    options.extras != null && typeof options.extras === "object" && !Array.isArray(options.extras)
      ? (options.extras as Record<string, unknown>)
      : null;
  const setup =
    extras?.setup != null && typeof extras.setup === "object" && !Array.isArray(extras.setup)
      ? (extras.setup as Record<string, unknown>)
      : null;

  const keyPresence = (obj: Record<string, unknown> | null | undefined, key: string): unknown => {
    if (!obj || !Object.prototype.hasOwnProperty.call(obj, key)) return "(absent)";
    const value = obj[key];
    if (value != null && typeof value === "object") {
      return sanitizeMetaDebugData(value);
    }
    return value;
  };

  return {
    config_id: keyPresence(options, "config_id"),
    response_type: keyPresence(options, "response_type"),
    override_default_response_type: keyPresence(options, "override_default_response_type"),
    extras: extras == null ? "(absent)" : sanitizeMetaDebugData(extras),
    "extras.sessionInfoVersion": keyPresence(extras, "sessionInfoVersion"),
    "extras.setup": keyPresence(extras, "setup"),
    auth_type: keyPresence(options, "auth_type"),
    scope: keyPresence(options, "scope"),
    solutionID: keyPresence(options, "solutionID"),
    solution_id: keyPresence(options, "solution_id"),
    "extras.setup.solutionID": keyPresence(setup, "solutionID"),
    "extras.setup.solution_id": keyPresence(setup, "solution_id"),
    feature: keyPresence(options, "feature"),
    "extras.feature": keyPresence(extras, "feature"),
    featureType: keyPresence(options, "featureType"),
    "extras.featureType": keyPresence(extras, "featureType"),
  };
}

export function summarizeFbLoginCallback(response: unknown): Record<string, unknown> {
  const r =
    response != null && typeof response === "object"
      ? (response as Record<string, unknown>)
      : null;
  const auth =
    r?.authResponse != null && typeof r.authResponse === "object"
      ? (r.authResponse as Record<string, unknown>)
      : null;

  return {
    status: typeof r?.status === "string" ? r.status : (r?.status ?? null),
    hasAuthResponse: auth != null,
    hasAuthResponseCode: typeof auth?.code === "string" && auth.code.length > 0,
    hasAuthResponseAccessToken: typeof auth?.accessToken === "string" && auth.accessToken.length > 0,
    hasAuthResponseUserID: typeof auth?.userID === "string" && auth.userID.length > 0,
    expiresIn: typeof auth?.expiresIn === "number" ? auth.expiresIn : null,
    errorCode: r?.error_code ?? r?.errorCode ?? null,
    errorType:
      typeof r?.error_type === "string"
        ? r.error_type.slice(0, 200)
        : typeof r?.errorType === "string"
          ? r.errorType.slice(0, 200)
          : null,
  };
}

export interface ParsedWaMessageSummary {
  correlationId: string | null;
  origin: string;
  payloadType: string;
  parseFailed?: boolean;
  type?: unknown;
  event?: unknown;
  current_step?: unknown;
  error_id?: unknown;
  hasWabaId?: boolean;
  hasPhoneNumberId?: boolean;
  hasBusinessId?: boolean;
  errorMessage?: string | null;
}

export function summarizeWaEmbeddedSignupMessage(
  event: { origin: string; data: unknown },
  correlationId: string | null,
): ParsedWaMessageSummary | null {
  if (!isExpectedMetaMessageOrigin(event.origin)) {
    return null;
  }

  const base: ParsedWaMessageSummary = {
    correlationId,
    origin: event.origin,
    payloadType: typeof event.data,
  };

  let parsed: unknown = event.data;
  if (typeof event.data === "string") {
    try {
      parsed = JSON.parse(event.data) as unknown;
    } catch {
      return { ...base, parseFailed: true };
    }
  } else if (event.data == null || typeof event.data !== "object") {
    return { ...base, parseFailed: true };
  }

  if (parsed == null || typeof parsed !== "object") {
    return { ...base, parseFailed: true };
  }

  const obj = parsed as Record<string, unknown>;
  const data =
    obj.data != null && typeof obj.data === "object" && !Array.isArray(obj.data)
      ? (obj.data as Record<string, unknown>)
      : null;

  const errorMessageCandidate =
    (typeof data?.error_message === "string" && data.error_message) ||
    (typeof data?.message === "string" && data.message) ||
    (typeof obj.error_message === "string" && obj.error_message) ||
    null;

  return {
    ...base,
    type: obj.type ?? null,
    event: obj.event ?? null,
    current_step: data?.current_step ?? obj.current_step ?? null,
    error_id: data?.error_id ?? obj.error_id ?? null,
    hasWabaId: Boolean(data?.waba_id),
    hasPhoneNumberId: Boolean(data?.phone_number_id),
    hasBusinessId: Boolean(data?.business_id),
    errorMessage: errorMessageCandidate ? String(errorMessageCandidate).slice(0, 500) : null,
  };
}

function pushEvent(entry: MetaDebugEvent): void {
  events.push(entry);
  if (events.length > MAX_EVENTS) {
    events.splice(0, events.length - MAX_EVENTS);
  }
  ensureMetaDebugWindowApi();
}

export function metaDebugLog(
  event: MetaDebugEventName,
  data: Record<string, unknown>,
  options?: { correlationId?: string | null; groupCollapsed?: boolean; asError?: boolean },
): void {
  if (!readMetaDebugEnabled()) return;
  try {
    const correlationId = options?.correlationId ?? activeCorrelationId ?? undefined;
    const sanitizedData = sanitizeMetaDebugData(data) as Record<string, unknown>;
    const entry: MetaDebugEvent = {
      ts: new Date().toISOString(),
      event,
      ...(correlationId ? { correlationId } : {}),
      data: sanitizedData,
    };
    pushEvent(entry);

    const payload = {
      ts: entry.ts,
      event: entry.event,
      ...(entry.correlationId ? { correlationId: entry.correlationId } : {}),
      ...sanitizedData,
    };

    const useGroup = options?.groupCollapsed !== false;
    if (useGroup && typeof console.groupCollapsed === "function") {
      console.groupCollapsed(`[MetaDebug] ${event}`);
      if (options?.asError) {
        console.error(payload);
      } else {
        console.info(payload);
      }
      console.groupEnd();
    } else if (options?.asError) {
      console.error("[MetaDebug]", payload);
    } else {
      console.info("[MetaDebug]", payload);
    }
  } catch {
    // Diagnostics must never interrupt signup.
  }
}

async function copyMetaDebugEvents(): Promise<void> {
  const json = JSON.stringify(events, null, 2);
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(json);
      console.info("[MetaDebug] Copied sanitized events to clipboard.");
      return;
    }
  } catch {
    // fall through to console dump
  }
  try {
    console.info("[MetaDebug] Clipboard unavailable; sanitized events:\n" + json);
  } catch {
    // ignore
  }
}

export function ensureMetaDebugWindowApi(): void {
  if (!readMetaDebugEnabled()) return;
  if (typeof window === "undefined") return;
  try {
    window.__MULTI_CHAT_META_DEBUG__ = {
      enabled: true,
      events,
      copy: async () => {
        try {
          await copyMetaDebugEvents();
        } catch {
          // ignore
        }
      },
    };
  } catch {
    // ignore
  }
}

export function attachMetaEmbeddedSignupMessageDiagnostics(): void {
  if (!readMetaDebugEnabled()) return;
  if (typeof window === "undefined") return;
  if (messageListenerAttached) return;
  messageListenerAttached = true;

  try {
    window.addEventListener("message", (event: MessageEvent) => {
      try {
        const summary = summarizeWaEmbeddedSignupMessage(event, activeCorrelationId);
        if (!summary) return;

        metaDebugLog("META_WINDOW_MESSAGE_RECEIVED", {
          origin: summary.origin,
          payloadType: summary.payloadType,
          ...(summary.parseFailed
            ? { parseFailed: true }
            : {
                type: summary.type,
                event: summary.event,
                current_step: summary.current_step,
                error_id: summary.error_id,
                hasWabaId: summary.hasWabaId,
                hasPhoneNumberId: summary.hasPhoneNumberId,
                hasBusinessId: summary.hasBusinessId,
              }),
        });

        if (summary.parseFailed) return;

        const eventName = typeof summary.event === "string" ? summary.event.toUpperCase() : "";
        const elapsedMs = getMetaEmbeddedSignupElapsedMs();

        if (eventName.includes("ERROR") || summary.error_id != null) {
          metaDebugLog(
            "META_EMBEDDED_SIGNUP_ERROR",
            {
              error_id: summary.error_id ?? null,
              current_step: summary.current_step ?? null,
              errorMessage: summary.errorMessage ?? null,
              elapsedMs,
            },
            { asError: true, groupCollapsed: false },
          );
        }

        if (eventName.includes("FINISH")) {
          metaDebugLog("META_EMBEDDED_SIGNUP_FINISH", {
            elapsedMs,
            hasWabaId: summary.hasWabaId ?? false,
            hasPhoneNumberId: summary.hasPhoneNumberId ?? false,
            hasBusinessId: summary.hasBusinessId ?? false,
          });
        }

        if (eventName.includes("CANCEL") || eventName.includes("ABORT")) {
          metaDebugLog("META_EMBEDDED_SIGNUP_CANCEL", {
            current_step: summary.current_step ?? null,
            elapsedMs,
          });
        }
      } catch {
        // never interrupt
      }
    });
  } catch {
    // never interrupt
  }
}

export function detectPopupBlockerHint(): "unknown" | "window.open_undefined" {
  try {
    if (typeof window === "undefined" || typeof window.open !== "function") {
      return "window.open_undefined";
    }
  } catch {
    return "unknown";
  }
  // Reliable detection requires opening a window; we intentionally do not.
  return "unknown";
}

/** Test helper: clear in-memory events (no-op when disabled). */
export function __resetMetaDebugEventsForTests(): void {
  events.length = 0;
  activeCorrelationId = null;
  attemptStartedAtMs = null;
}

/** Test helper: read a copy of stored events. */
export function __getMetaDebugEventsForTests(): MetaDebugEvent[] {
  return events.map((e) => ({
    ...e,
    data: { ...e.data },
  }));
}
